/**
 * Post-auth redirect utilities
 *
 * Persists the URL a logged-out user was viewing so that after sign-up and
 * onboarding they are returned to where they started rather than the homepage.
 *
 * sessionStorage is used because:
 *  - It survives full-page navigations within the same browser tab, including
 *    the external Cognito OAuth round-trip.
 *  - It is cleared automatically when the tab is closed, preventing stale
 *    redirects from surfacing in future sessions.
 */

const POST_AUTH_REDIRECT_KEY = 'gt_post_auth_redirect';

/**
 * Paths that must never be stored as a post-auth redirect destination –
 * storing them would cause an infinite loop or land the user in an undesired
 * auth/onboarding state.
 */
const BLOCKED_PATHS = ['/onboarding', '/auth/callback', '/auth'];

/**
 * Returns true when `path` is a safe destination to redirect to after auth.
 * Rejects empty strings, the home page (no value in storing it explicitly),
 * and any auth/onboarding paths.
 */
function isValidRedirectPath(path: string | null | undefined): path is string {
  if (!path || path === '/') return false;
  return !BLOCKED_PATHS.some((blocked) => path.startsWith(blocked));
}

/**
 * Saves `path` to sessionStorage so it can be retrieved after the onboarding
 * flow completes. No-ops if `path` is not a valid redirect destination.
 */
export function setPostAuthRedirect(path: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  if (!isValidRedirectPath(path)) return;

  try {
    sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, path);
  } catch {
    // sessionStorage may be unavailable in some private-browsing environments.
  }
}

/**
 * Reads the stored redirect path, removes it from sessionStorage, and returns
 * it. Returns `null` if nothing was stored or the stored value is no longer
 * considered valid.
 */
export function getAndClearPostAuthRedirect(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
    return isValidRedirectPath(stored) ? stored : null;
  } catch {
    return null;
  }
}
